import BaseMarker from "./BaseMarker";
import * as THREE from 'three';
import { markRaw } from 'vue';

export default class CameraHelperMarker extends BaseMarker {
    imageHeight = null;
    imageWidth = null;
    imageName = null;
    focalX = null;
    focalY = null;
    opticCenterX = null;
    opticCenterY = null;
    cameraFar = null;
    
    constructor(helper){
        super(helper.id, helper.type, helper.assetId, helper.scanId, helper.transform, helper.eTag);
        this.imageName = helper.imageName;
        this.imageWidth = Number(helper.imageWidth);
        this.imageHeight = Number(helper.imageHeight);
        this.focalX = Number(helper.cameraFocalX);
        this.focalY = Number(helper.cameraFocalY);
        this.opticCenterX = Number(helper.cameraOpticalCenterX);
        this.opticCenterY = Number(helper.cameraOpticalCenterY);
        this.cameraFar = Number(helper.cameraFar) ?? 10;

        this.generateMesh();
    }

    generateMesh(){
        const camera = new THREE.PerspectiveCamera();
          
        camera.position.set(
            Number(this.transform.position.x),
            Number(this.transform.position.y), // Swap Y and Z for Z-up to Y-up conversion
            Number(this.transform.position.z)
        );

        const rotation = new THREE.Euler(
                  THREE.MathUtils.degToRad(Number(this.transform.rotation.x)),
                  THREE.MathUtils.degToRad(Number(this.transform.rotation.y)),  
                  THREE.MathUtils.degToRad(Number(this.transform.rotation.z)),
                  this.transform.rotation.order
                );

        camera.setRotationFromEuler(rotation);
            
        // Set default values for FOV and aspect ratio
        const defaultFov = 51.447414;
        const defaultAspect = 1.78;
        
        // Calculate aspect ratio and FOV, using defaults if needed
        const aspect = this.imageWidth && this.imageHeight ? this.imageWidth / this.imageHeight : defaultAspect;
        const fov = this.focalY && this.imageHeight ? (2 * Math.atan(this.imageHeight / (2 * this.focalY)) * (180 / Math.PI)) : defaultFov;
        
        camera.fov = fov;
        camera.aspect = aspect;
        camera.near = 0.1;        
        camera.far = this.cameraFar; // Adjust far plane as needed (default = 15)
        
        // Update the projection matrix and world matrix
        camera.updateProjectionMatrix();
        camera.updateMatrixWorld(true);

        // Create a CameraHelper for the temporary camera
        const helper = new THREE.CameraHelper(camera);
        helper.position.x = camera.position.x;
        helper.position.y = camera.position.y;
        helper.position.z = camera.position.z;
        helper.visible = false;

        // The full frustum wireframe is drawn only when the camera is SELECTED — otherwise a scene
        // full of cameras is unreadable spaghetti. By default we show just a small clickable dot at
        // the camera position (below); the frustum lines stay hidden until the viewer selects it.
        helper.material.visible = false;
        this.originalFar = camera.far;   // restore point after a "show projection" zoom

        // Small clickable camera glyph at the camera position (local origin — the helper sits at the
        // apex). A cone pointing along the view direction, so which way each camera faces reads at a
        // glance (like a photogrammetry camera gizmo). ModelViewer scales it to the model size once
        // loaded; userData.markerId lets the viewer's raycaster resolve which camera was clicked.
        // Elongated cone whose BASE sits at the camera position and TIP points along the view
        // direction, so each camera clearly reads as "looking this way".
        const coneGeo = new THREE.ConeGeometry(0.5, 2.2, 20);
        coneGeo.translate(0, 1.1, 0);   // base at the origin (camera apex), tip at +Y before rotating
        const apex = new THREE.Mesh(coneGeo, new THREE.MeshBasicMaterial({ color: 0xa78bfa }));
        // The CameraHelper's matrix IS the camera's world matrix (matrixAutoUpdate=false), so this
        // cone — a child of the helper — already inherits the camera's per-camera orientation. It only
        // needs the local flip that points its +Y tip down the camera's forward axis (−Z). Do NOT also
        // apply camera.quaternion here or the rotation is doubled. Flip to (0,0,1) if it reads reversed.
        apex.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, -1));
        apex.name = 'camera-apex';
        apex.scale.setScalar(0.05);
        apex.userData.markerId = this.id;
        apex.userData.isCameraApex = true;
        helper.add(apex);
        this.apex = markRaw(apex);

        // We can reference this field for intersection.
        helper.userData.imageName = this.imageName;
        helper.userData.markerId = this.id;
        this.meshGroup = markRaw(helper);
    }
}